import { makeAutoObservable, runInAction } from 'mobx'
import { apiClient } from '@/services/api'
import { websocketClient, type WebSocketMessage } from '@/services/websocket'
import type {
  TeamExecution,
  TeamExecutionStatus,
  AgentMessage,
  PaginatedResponse,
} from '@/types'

class TeamExecutionStore {
  executions: TeamExecution[] = []
  selectedExecution: TeamExecution | null = null
  messages: AgentMessage[] = []
  isLoading = false
  error: string | null = null

  // Pagination
  currentPage = 1
  pageSize = 10
  totalExecutions = 0

  // Filters
  statusFilter: TeamExecutionStatus | 'all' = 'all'

  constructor() {
    makeAutoObservable(this)
  }

  // Computed values
  get filteredExecutions() {
    const executions = this.executions || []
    return executions.filter((execution) => {
      const matchesStatus =
        this.statusFilter === 'all' || execution.status === this.statusFilter

      return matchesStatus
    })
  }

  get hasActiveFilters() {
    return this.statusFilter !== 'all'
  }

  get messagesByIteration() {
    const grouped: Record<number, AgentMessage[]> = {}

    this.messages.forEach((message) => {
      if (!grouped[message.iteration]) {
        grouped[message.iteration] = []
      }
      grouped[message.iteration].push(message)
    })

    return grouped
  }

  get isExecutionRunning() {
    return (
      this.selectedExecution?.status === 'RUNNING' ||
      this.selectedExecution?.status === 'PENDING'
    )
  }

  // Actions
  async fetchExecutions(teamId: string) {
    this.isLoading = true
    this.error = null

    try {
      const response: PaginatedResponse<TeamExecution> =
        await apiClient.getTeamExecutions(teamId, {
          skip: (this.currentPage - 1) * this.pageSize,
          limit: this.pageSize,
        })

      runInAction(() => {
        this.executions = response.items
        this.totalExecutions = response.total
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.executions = []
        this.error = error.response?.data?.detail || 'Failed to fetch executions'
        this.isLoading = false
      })
    }
  }

  async fetchExecutionById(executionId: string) {
    this.isLoading = true
    this.error = null

    try {
      const execution = await apiClient.getTeamExecution(executionId)

      runInAction(() => {
        this.selectedExecution = execution
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch execution'
        this.isLoading = false
      })
    }
  }

  async executeTeam(teamId: string, inputData: Record<string, any>) {
    this.isLoading = true
    this.error = null

    try {
      const execution = await apiClient.executeTeam(teamId, inputData)

      runInAction(() => {
        this.executions = [execution, ...this.executions]
        this.totalExecutions += 1
        this.selectedExecution = execution
        this.isLoading = false
      })

      return execution
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to execute team'
        this.isLoading = false
      })
      throw error
    }
  }

  async cancelExecution(executionId: string) {
    this.isLoading = true
    this.error = null

    try {
      const execution = await apiClient.cancelTeamExecution(executionId)

      runInAction(() => {
        this.executions = this.executions.map((exec) =>
          exec.id === executionId ? execution : exec
        )
        if (this.selectedExecution?.id === executionId) {
          this.selectedExecution = execution
        }
        this.isLoading = false
      })

      return execution
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to cancel execution'
        this.isLoading = false
      })
      throw error
    }
  }

  async fetchMessages(executionId: string) {
    this.isLoading = true
    this.error = null

    try {
      const messages = await apiClient.getExecutionMessages(executionId)

      runInAction(() => {
        this.messages = messages
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.messages = []
        this.error = error.response?.data?.detail || 'Failed to fetch messages'
        this.isLoading = false
      })
    }
  }

  async refreshExecution() {
    if (this.selectedExecution) {
      await this.fetchExecutionById(this.selectedExecution.id)
      await this.fetchMessages(this.selectedExecution.id)
    }
  }

  // WebSocket for real-time updates
  private wsHandlers: Map<string, (data: any) => void> = new Map()

  /**
   * Subscribe to real-time updates for a team execution
   */
  subscribeToExecution(executionId: string) {
    this.unsubscribeFromExecution() // Clean up existing subscriptions

    // Create handler for team execution updates
    const handler = (message: WebSocketMessage) => {
      if (message.execution_id === executionId) {
        runInAction(() => {
          // Update selected execution
          if (this.selectedExecution?.id === executionId) {
            this.selectedExecution = {
              ...this.selectedExecution,
              status: message.status,
              ...(message.data || {}),
            }
          }

          // Update in executions list if present
          const index = this.executions.findIndex((e) => e.id === executionId)
          if (index !== -1) {
            this.executions[index] = {
              ...this.executions[index],
              status: message.status,
              ...(message.data || {}),
            }
          }
        })

        // Fetch final messages if execution is complete
        if (
          message.status === 'COMPLETED' ||
          message.status === 'FAILED' ||
          message.status === 'CANCELLED'
        ) {
          this.fetchMessages(executionId)
        }
      }
    }

    // Register handler
    this.wsHandlers.set(executionId, handler)
    websocketClient.on('team_execution.update', handler)

    // Subscribe to the execution channel
    websocketClient.subscribe(`team_execution:${executionId}`)

    console.log(`Subscribed to team execution updates: ${executionId}`)
  }

  /**
   * Unsubscribe from real-time updates
   */
  unsubscribeFromExecution() {
    this.wsHandlers.forEach((handler, executionId) => {
      websocketClient.off('team_execution.update', handler)
      websocketClient.unsubscribe(`team_execution:${executionId}`)
    })
    this.wsHandlers.clear()
  }

  // Filter actions
  setStatusFilter(status: TeamExecutionStatus | 'all') {
    this.statusFilter = status
  }

  clearFilters() {
    this.statusFilter = 'all'
  }

  // Pagination actions
  setPage(page: number) {
    this.currentPage = page
  }

  setPageSize(size: number) {
    this.pageSize = size
    this.currentPage = 1 // Reset to first page
  }

  // Utility actions
  clearError() {
    this.error = null
  }

  clearSelectedExecution() {
    this.unsubscribeFromExecution()
    this.selectedExecution = null
    this.messages = []
  }

  cleanup() {
    this.unsubscribeFromExecution()
  }
}

export const teamExecutionStore = new TeamExecutionStore()
